import apiClient from '@/api/ApiClient';

/**
 * Hands a blob to the browser as a saved file. The object-URL/anchor-click dance lives here so
 * every "download this" button in the app shares one implementation.
 */
function saveBlobAsFile(blob: Blob, filename: string): void {
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(blobUrl);
}

/**
 * Fetches a file from an authenticated API endpoint and triggers a browser download for it.
 * Returns false without downloading anything when the endpoint answers that it has no file to
 * give (204 No Content, or an empty body) - callers that can hit that case report it themselves.
 */
export async function downloadFileFromApi(url: string, filename: string, token: string): Promise<boolean> {
  const res = await apiClient.get(url, {
    headers: { Authorization: `Bearer ${token}` },
    responseType: 'blob',
  });

  const blob = res.data as Blob | undefined;
  if (res.status === 204 || !blob || blob.size === 0) {
    return false;
  }

  saveBlobAsFile(blob, filename);
  return true;
}

/**
 * Opens a file from an authenticated API endpoint in a new tab, for a «צפייה» button beside a
 * «הורדה» one - the standing-order form, which the server generates per fund and serves as an
 * attachment.
 *
 * The endpoint needs an Authorization header, so the tab cannot simply be pointed at the URL. The
 * blank tab is therefore opened synchronously, inside the click, and navigated once the bytes have
 * arrived - a window.open() after the await is what a popup blocker stops. Returns false when the
 * endpoint has nothing to give, or when the tab was blocked anyway.
 */
export async function openFileFromApi(url: string, token: string): Promise<boolean> {
  const tab = window.open('', '_blank');

  try {
    const res = await apiClient.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });

    const blob = res.data as Blob | undefined;
    if (res.status === 204 || !blob || blob.size === 0) {
      tab?.close();
      return false;
    }

    if (!tab) return false;

    tab.location.href = URL.createObjectURL(blob);
    return true;
  } catch (error) {
    tab?.close();
    throw error;
  }
}

/**
 * Downloads a document that lives in blob storage, addressed by the download link that every
 * document DTO carries (its `uri` / `url` field). Goes through our own API rather than hitting the
 * link directly, because the link is signed for inline viewing and the browser is not allowed to
 * fetch it cross-origin - the API re-serves the same bytes as an attachment. The link is the
 * credential, so no token is needed on top of it, and the API only reads the file path out of it -
 * so this keeps working on a page that has been open longer than the link stays valid for.
 */
export async function downloadStoredDocument(uri: string, filename: string): Promise<void> {
  const res = await apiClient.get(
    `documents/download?url=${encodeURIComponent(uri)}&fileName=${encodeURIComponent(filename)}`,
    { responseType: 'blob' }
  );

  saveBlobAsFile(res.data as Blob, filename);
}
